<template>
    <div class="modal-mask">
        <div class="modal-wrapper">
            <div class="modal-container" style="width: 50%;">
                <div class="modal-header">
                    <slot name="header">
                        <h3 class="form-header">Clinician Bag Check Out</h3>
                        <button @click="closeModal" class="red-close-button">
                            <i class="pi pi-times" style='padding: 0'></i>
                        </button>
                    </slot>
                </div>
                <div class="modal-body">
                    <slot name="body">
                        <form class="action-modal">
                            <div class="form-section-container" style="grid-template-columns: 1fr 1fr; width: 100%;">
                                <!-- LEFT PANEL: Metadata -->
                                <div class="left-form-section-panel" style="min-height: fit-content; padding-right: 10px;">
                                    
                                    <div style="margin-bottom: 15px;">
                                        <p style="text-align: left; font-weight: bold; margin-bottom: 5px;">Mission Number</p>
                                        <input v-model="external_number" type="text" placeholder="Mission Number..." style="width: 100%; border-radius: 4px; padding: 8px; border: 1px solid #ccc;" />
                                    </div>

                                    <div style="margin-bottom: 15px;">
                                        <p style="text-align: left; font-weight: bold; margin-bottom: 5px;">Tag</p>
                                        <input v-model="tag" type="text" placeholder="Tag / Bag ID..." style="width: 100%; border-radius: 4px; padding: 8px; border: 1px solid #ccc;" />
                                    </div>

                                    <div style="margin-bottom: 15px;">
                                        <p style="text-align: left; font-weight: bold; margin-bottom: 5px;">Clinician <span style="color:red">*</span></p>
                                        <Dropdown
                                            v-model="selectedAnesthetist"
                                            ref="clinicianDropdown"
                                            @keyup="debounceSearchAnesthetist"
                                            @focus="onClinicianFocus"
                                            :loading="loadingAnesthetists"
                                            :options="anesthetistList"
                                            optionLabel="full_name"
                                            :editable="true"
                                            style="width: 100%;"
                                            placeholder="Search Clinician" 
                                        />
                                    </div>

                                    <div>
                                        <p style="text-align: left; font-weight: bold; margin-bottom: 5px;">Notes</p>
                                        <textarea v-model="notes" rows="6" style="width: 100%; border-radius: 4px; padding: 8px;" placeholder="Optional notes for this checkout..."></textarea>
                                    </div>
                                </div>
                                
                                <!-- RIGHT PANEL: Drugs -->
                                <div class="right-form-section-panel" style="display: flex; flex-direction: column; padding-left: 10px;">
                                    <p style="text-align: left; font-weight: bold; margin-bottom: 5px;">Select Medication <span style="color:red">*</span></p>
                                    
                                    <!-- The batch picker gets its own line. Its label carries the batch name, the expiry
                                         and the remaining count, which left the medication picker with no room at all
                                         when the two shared a row. It renders nothing for unbatched stock, so the
                                         second line only appears when there is a batch to choose. -->
                                    <div class="right-form-section-drug-entry" style="display: flex; flex-direction: column; margin-bottom: 15px;">
                                        <div style="display: flex; width: 100%;">
                                            <Dropdown
                                                v-model="stock"
                                                ref="med"
                                                @keyup="debounceSearchDrugs"
                                                @focus="onDrugFocus"
                                                @change="drugSelect"
                                                :loading="loadingDrugs"
                                                :options="drugList"
                                                optionLabel="full_name"
                                                :editable="true"
                                                style="flex: 1; min-width: 0; margin-right: 10px;"
                                                optionDisabled="disabled"
                                                :emptyMessage="stockPickerEmptyMessage(stock)"
                                                placeholder="Search Stock"
                                            />
                                            <input v-model="qty" min="0" type="number" placeholder="Qty" style="width: 80px; margin-right: 10px;" />
                                            <p :class="batchRequiredAndMissing ? 'submit-button grey-button' : 'submit-button blue-button'" style="padding: 10px; margin: 0;" @click="() => { if (!batchRequiredAndMissing) { addDrug() } }">Add</p>
                                        </div>
                                        <BatchSelect :stockItem="stock" v-model="selectedBatch" />
                                    </div>

                                    <div class="right-form-section-drug-container" style="overflow-y: auto; max-height: 300px;">
                                        <table style="border-collapse: collapse; width: 100%;" v-if="drugs.length > 0">
                                            <tr>
                                                <th style="text-align: left;">Drug</th>
                                                <th style="text-align: left; width: 90px;" v-if="showBatchColumn">Batch no.</th>
                                                <th style="text-align: left; width: 60px;">Stock</th>
                                                <th style="text-align: left; width: 60px;">Qty</th>
                                                <th style="width: 40px;"></th>
                                            </tr>
                                            <tr v-for="(item, index) in drugs" :key="index" style="border-bottom: 1px solid #ccc;">
                                                <td style="padding: 10px 0; max-width: 250px; cursor: pointer;" 
                                                    @click="expandedDrugs.includes(index) ? expandedDrugs.splice(expandedDrugs.indexOf(index), 1) : expandedDrugs.push(index)"
                                                    :title="expandedDrugs.includes(index) ? 'Click to collapse' : 'Click to expand: ' + item.stock_name">
                                                    <div :style="expandedDrugs.includes(index) ? { whiteSpace: 'normal' } : { display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }">
                                                        {{ item.stock_name }}
                                                    </div>
                                                </td>
                                                <td style="padding: 8px 0;" v-if="showBatchColumn">{{ item.batch_number }}</td>
                                                <td style="padding: 8px 0;">{{ item.qty_left }}</td>
                                                <td style="padding: 8px 0;">{{ item.qty }}</td>
                                                <td style="padding: 8px 0;">
                                                    <button type="button" @click="removeDrug(index)" class="red-close-button small-button">
                                                        <i class="pi pi-times" style="padding: 0;"></i>
                                                    </button>
                                                </td>
                                            </tr>
                                        </table>
                                        <p v-else style="color: #666; font-style: italic;">No medications added yet.</p>
                                    </div>
                                </div>
                            </div>
                        </form>
                    </slot>
                </div>
                <p style="color: red; font-weight: 500; font-size: 1em; text-align: center; margin-top: 10px;" v-if="errorMessage">{{ errorMessage }}</p>
                <div class="modal-footer" style="display: flex; justify-content: flex-end; padding: 15px 20px;">
                    <slot name="footer">
                        <p class="regular-button error-button" style="color: white; margin-right: 10px;" @click="closeModal">Cancel</p>
                        <p class="regular-button green-button" @click="submitCheckout">Checkout</p>
                    </slot>
                </div>
            </div>
        </div>
        <SignatureModal v-if="displaySignatureModal" :payload="realSubmit" @isSuccessful="isSuccessful" @close="close" :saveContext="saveContext" endpoint="/theatre-checkout" />
    </div>
</template>

<script>
import SignatureModal from '@/components/modals/SignatureModal.vue';
import axios from 'axios';
import { isPickerEmpty, pickerSearchTerm, stockPickerEmptyMessage } from '@/services/pickers';
import { debounce } from 'debounce';
import { decorateStockItems } from '@/services/stockItemDisplay';
import BatchSelect from '@/components/BatchSelect.vue';
import { drugsRequireDoubleSig } from '@/services/doubleSignature';

export default {
    components: {
        BatchSelect,
        SignatureModal
    },
    data() {
        return {
            external_number: '',
            tag: '',
            displaySignatureModal: false,
            
            selectedAnesthetist: null,
            anesthetistList: [],
            loadingAnesthetists: false,

            stock: '',
            drugList: [],
            loadingDrugs: false,
            qty: null,
            
            drugs: [],
            // The batch chosen beside the picker for the row being added. BatchSelect clears it whenever
            // the medication changes.
            selectedBatch: null,
            expandedDrugs: [],
            notes: '',
            
            errorMessage: '',
            realSubmit: null,
            saveContext: null
        }
    },
    computed: {
        // Show the batch column when any row carries a batch.
        showBatchColumn() {
            return Array.isArray(this.drugs) && this.drugs.some(row => row && row.batch_number);
        },
        // The stock holds batches and none has been chosen, so the row cannot be added. The backend
        // enforces the same thing; this only saves the user a rejected submit.
        batchRequiredAndMissing() {
            const batches = (this.stock && Array.isArray(this.stock.batches)) ? this.stock.batches : [];
            // The flag is checked here too, so a stale response carrying batches cannot leave Add
            // permanently disabled for a facility that has no batch dropdown to satisfy it with.
            return this.$store.getters.isBatchTrackingEnabled && batches.length > 0 && !this.selectedBatch;
        },
        requiresDoubleSig() {
            const facility = this.$store.getters.getCurrentLocation?.facility;
            return drugsRequireDoubleSig(this.drugs.filter(d => d.qty > 0), facility);
        }
    },
    methods: {
        stockPickerEmptyMessage,
        formatDrugName(stockItem) {
            if (!stockItem || !stockItem.alias) return 'Unknown Medication';
            const alias = stockItem.alias;
            const generic = alias.generic_name || (alias.generic_drug && alias.generic_drug.name);
            return generic ? `(${generic}) ${alias.name}` : alias.name;
        },
        closeModal() {
            this.$emit('close', true);
        },
        close(value) {
            if (value) {
                this.displaySignatureModal = false;
            }
        },
        searchClinician(fetchDefault = false) {
            const searchTerm = pickerSearchTerm(this.selectedAnesthetist);
            if (searchTerm || fetchDefault) {
                this.loadingAnesthetists = true;
                axios.post('/get-clinician', { name: searchTerm }, this.$store.state.header).then(res => {
                    this.anesthetistList = res.data.data.map(a => {
                        a.full_name = a.first_name + ' ' + a.last_name;
                        return a;
                    });
                    this.loadingAnesthetists = false;
                    if (fetchDefault && this.$refs.clinicianDropdown) {
                        this.$refs.clinicianDropdown.show();
                    }
                }).catch(err => {
                    this.loadingAnesthetists = false;
                    console.error(err);
                });
            }
        },
        debounceSearchAnesthetist: debounce(function() {
            this.searchClinician(false);
        }, 500),
        onClinicianFocus() {
            if (isPickerEmpty(this.selectedAnesthetist)) {
                this.searchClinician(true);
            }
        },
        searchDrugs(fetchDefault = false) {
            const searchTerm = pickerSearchTerm(this.stock);
            if (searchTerm || fetchDefault) {
                this.loadingDrugs = true;
                axios.post('/get-stock-items', { drug: searchTerm }, this.$store.state.header).then(res => {
                    this.drugList = decorateStockItems(res.data);
                    this.loadingDrugs = false;
                    // Opened on every response, not just the focus preload: an empty result is
                    // what the picker most needs to report, and the message that says so only
                    // renders while the overlay is up.
                    if (this.$refs.med) {
                        this.$refs.med.show();
                    }
                }).catch(err => {
                    this.loadingDrugs = false;
                    console.error(err);
                });
            }
        },
        debounceSearchDrugs: debounce(function() {
            this.searchDrugs(false);
        }, 500),
        onDrugFocus() {
            if (isPickerEmpty(this.stock)) {
                this.searchDrugs(true);
            }
        },
        drugSelect(item) {
            if (typeof item === 'string' || !item) return;
        },
        addDrug() {
            if (!this.stock || typeof this.stock === 'string') {
                this.errorMessage = "Please select a valid medication from the list.";
                return;
            }
            if (!this.qty || this.qty <= 0) {
                this.errorMessage = "Please enter a valid quantity.";
                return;
            }

            // Don't allow checking out more than is on hand. This has to be validated against the
            // running total for the stock item rather than just the row being added: adding the
            // same item twice created two independently-validated rows, so 2 + 2 of an item with
            // 3 on hand was accepted. If the API didn't send qty_left the check is skipped rather
            // than blocking a legitimate checkout - the server is still the authority.
            const requestedQty = Number(this.qty);
            const availableQty = Number(this.stock.qty_left);
            const alreadyAddedQty = this.drugs
                .filter(d => d.stock_item && d.stock_item.uuid === this.stock.uuid)
                .reduce((total, d) => total + Number(d.qty), 0);

            if (!isNaN(availableQty) && (alreadyAddedQty + requestedQty) > availableQty) {
                const remaining = availableQty - alreadyAddedQty;
                this.errorMessage = remaining > 0
                    ? `Only ${remaining} of ${this.formatDrugName(this.stock)} available - reduce the quantity.`
                    : `${this.formatDrugName(this.stock)} has already been added up to the available quantity of ${availableQty}.`;
                return;
            }

            this.drugs.unshift({
                stock_item: this.stock,
                stock_name: this.formatDrugName(this.stock),
                batch_number: this.selectedBatch ? this.selectedBatch.batch_name : null,
                batch_uuid: this.selectedBatch ? this.selectedBatch.uuid : null,
                qty_left: this.stock.qty_left,
                qty: requestedQty
            });
            
            this.stock = '';
            this.qty = null;
            this.selectedBatch = null;
            this.errorMessage = '';
        },
        removeDrug(index) {
            this.drugs.splice(index, 1);
        },
        submitCheckout() {
            if (!this.selectedAnesthetist || typeof this.selectedAnesthetist === 'string') {
                this.errorMessage = "Clinician selection is required.";
                return;
            }
            if (this.drugs.length === 0) {
                this.errorMessage = "At least one medication is required.";
                return;
            }

            // Map drugs for backend payload
            const formattedDrugs = this.drugs.map(d => {
                return {
                    stock_item: d.stock_item,
                    qty: d.qty,
                    // Chosen on the way out. A check-in carries back whatever went out rather than
                    // asking again.
                    batch_uuid: d.batch_uuid ?? null,
                    tags: d.stock_item?.alias?.tags || []
                };
            });

            this.realSubmit = {
                type: 'clinician_hold',
                external_number: this.external_number,
                tag: this.tag,
                drugs: formattedDrugs,
                notes: this.notes,
                clinician_id: this.selectedAnesthetist.uuid
            };

            this.saveContext = {
                type: 'ClinicianHoldCheckout',
                patient: `Mission: ${this.external_number} - Tag: ${this.tag}`,
                location: this.$store.state.currentLocation.name,
                drugs: this.drugs.map(d => ({ drug: d.stock_name, qty: d.qty })),
                doubleSig: this.requiresDoubleSig
            };

            this.errorMessage = '';
            this.displaySignatureModal = true;
        },
        isSuccessful(value) {
            if (value) {
                this.$emit('close', true, 'success');
            }
        }
    }
}
</script>

<style scoped>
/* Inherit global modal styles from main.scss, add any specifics if needed */

/* BatchSelect carries a left margin for sitting beside a picker. On its own line it lines up with
   the medication picker above it instead. */
.right-form-section-drug-entry :deep(.batch-select) {
    margin-left: 0;
    margin-top: 10px;
}
</style>
